/**
 * Reglas de cálculo del Agente TA Metrics (RNF-08): ETAPAS_ABIERTAS y
 * MAPEO_OWNERS como configuración, editables por el admin de la sección (TA
 * aprueba los cambios). Cambiarlas cambia TODAS las métricas: qué OL cuenta
 * como búsqueda Abierta y a quién se atribuye una búsqueda compartida.
 * Se editan como texto (una etapa por línea; "Luis/Maflo = Maflo" por línea).
 */

import { currentLocale } from '@/lib/locale';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useSaveTaRules, useTaRules } from '@/hooks/useTalentAcquisition';
import { lineasInvalidas, mapeoFromText, mapeoToText, nivelesFromText, nivelesToText } from './ta-rules-text';

export function TaRulesCard() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.rules';
  const rules = useTaRules();
  const save = useSaveTaRules();
  const [etapas, setEtapas] = useState('');
  const [mapeo, setMapeo] = useState('');
  const [niveles, setNiveles] = useState('');
  const [terminos, setTerminos] = useState('');
  const [loadedAt, setLoadedAt] = useState<string | null>(null);

  // Se cargan una vez por versión guardada; editar no se pisa con refetches
  useEffect(() => {
    if (!rules.data) return;
    const version = rules.data.updatedAt || 'default';
    if (loadedAt === version) return;
    setEtapas(rules.data.etapasAbiertas.join('\n'));
    setMapeo(mapeoToText(rules.data.mapeoOwners));
    setNiveles(nivelesToText(rules.data.seniorityNiveles));
    setTerminos(mapeoToText(rules.data.terminosNegocio));
    setLoadedAt(version);
  }, [rules.data, loadedAt]);

  const parsed = useMemo(() => ({
    etapasAbiertas: etapas.split('\n').map((l) => l.trim()).filter(Boolean),
    mapeoOwners: mapeoFromText(mapeo),
    seniorityNiveles: nivelesFromText(niveles),
    terminosNegocio: mapeoFromText(terminos),
  }), [etapas, mapeo, niveles, terminos]);
  const invalidLines = useMemo(() => lineasInvalidas(mapeo), [mapeo]);
  const invalidNiveles = useMemo(() => lineasInvalidas(niveles), [niveles]);
  const invalidTerminos = useMemo(() => lineasInvalidas(terminos), [terminos]);
  const dirty = !!rules.data && (
    etapas !== rules.data.etapasAbiertas.join('\n') || mapeo !== mapeoToText(rules.data.mapeoOwners)
    || niveles !== nivelesToText(rules.data.seniorityNiveles) || terminos !== mapeoToText(rules.data.terminosNegocio)
  );
  const canSave = dirty && parsed.etapasAbiertas.length > 0 && invalidLines.length === 0 && invalidNiveles.length === 0 && invalidTerminos.length === 0 && !save.isPending;

  const restoreDefaults = () => {
    if (!rules.data) return;
    setEtapas(rules.data.defaults.etapasAbiertas.join('\n'));
    setMapeo(mapeoToText(rules.data.defaults.mapeoOwners));
    setNiveles(nivelesToText(rules.data.defaults.seniorityNiveles));
    setTerminos(mapeoToText(rules.data.defaults.terminosNegocio));
  };

  if (rules.isLoading) return <div className="flex justify-center py-8"><WinclapLoader /></div>;
  if (rules.isError || !rules.data) {
    // Un fallo de carga se ve como fallo, no como "esta card no existe"
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t(`${k}.title`)}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <p className="flex items-center gap-1.5 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />{t(`${k}.load_error`)}</p>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => rules.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" /> {t(`${k}.retry`)}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">{t(`${k}.title`)}</CardTitle>
          <CardDescription>{t(`${k}.desc`)}</CardDescription>
        </div>
        <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={restoreDefaults} title={t(`${k}.restore_defaults`)}>
          <RotateCcw className="h-3.5 w-3.5" /> {t(`${k}.restore_defaults`)}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t(`${k}.etapas_label`)}</label>
            <p className="text-xs text-muted-foreground">{t(`${k}.etapas_help`)}</p>
            <Textarea value={etapas} onChange={(e) => setEtapas(e.target.value)} rows={8} className="font-mono text-xs" spellCheck={false} />
            {parsed.etapasAbiertas.length === 0 && (
              <p className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3 w-3" />{t(`${k}.etapas_empty`)}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t(`${k}.mapeo_label`)}</label>
            <p className="text-xs text-muted-foreground">{t(`${k}.mapeo_help`)}</p>
            <Textarea value={mapeo} onChange={(e) => setMapeo(e.target.value)} rows={8} className="font-mono text-xs" spellCheck={false} />
            {invalidLines.length > 0 && (
              <p className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3 w-3" />{t(`${k}.mapeo_invalid`, { line: invalidLines[0] })}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t(`${k}.niveles_label`)}</label>
            <p className="text-xs text-muted-foreground">{t(`${k}.niveles_help`)}</p>
            <Textarea value={niveles} onChange={(e) => setNiveles(e.target.value)} rows={6} className="font-mono text-xs" spellCheck={false} />
            {invalidNiveles.length > 0 && (
              <p className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3 w-3" />{t(`${k}.niveles_invalid`, { line: invalidNiveles[0] })}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t(`${k}.terminos_label`)}</label>
            <p className="text-xs text-muted-foreground">{t(`${k}.terminos_help`)}</p>
            <Textarea value={terminos} onChange={(e) => setTerminos(e.target.value)} rows={6} className="font-mono text-xs" spellCheck={false} />
            {invalidTerminos.length > 0 && (
              <p className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3 w-3" />{t(`${k}.terminos_invalid`, { line: invalidTerminos[0] })}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {rules.data.updatedAt
              ? t(`${k}.last_saved`, { by: rules.data.updatedBy, at: new Date(rules.data.updatedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' }) })
              : t(`${k}.using_defaults`)}
          </p>
          <Button onClick={() => save.mutate({ ...parsed, baseUpdatedAt: rules.data?.updatedAt ?? '' })} disabled={!canSave}>
            {save.isPending ? t('talent_acquisition.governance.saving') : t(`${k}.save`)}
          </Button>
        </div>
        <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t(`${k}.warning`)}
        </p>
      </CardContent>
    </Card>
  );
}
